import { apiServices } from '@/services/api';
import { useFundraisingDetailStore } from '@/store/fundraising-detail.store';
import { clearLoading, setLoading } from '@/services/utils/loading.service';
import { FundraisingDetail } from '@/models/fundraising-detail.model';
import { FundraisingDonationModel } from '@/models/fundraising-donation.model';
import { getFundraisingProject } from './details-display.service';
import { generatePaginationQuery } from '../utils/donation-pagination-query.service';

export const loadFundraisingProject = async (id: string): Promise<boolean> => {
  setFundraisingProjectLoading(true);
  resetPagination();
  const detail = await apiServices.fundraisingDetail(id);
  await fundraisingProjectDonations(id);
  setFundraisingProject(detail.data);
  setFundraisingProjectLoaded(true);
  if (detail.isAxiosError) {
    return false;
  }
  return true;
};

export const fundraisingProjectDonations = async (
    projectId: string
): Promise<boolean> => {
  const query = generatePaginationQuery();
  const donations = await apiServices.fundraisingProjectDonationsService(
      projectId,
      query
  );
  if (!donations.isAxiosError) {
    const projectStore = useFundraisingDetailStore();
    projectStore.setProjectDonations(donations.data);
    projectStore.setPaginationTotal(donations.meta.total ?? 0);
    clearLoading();
    return true;
  } else {
    clearLoading();
    return false;
  }
}

export const checkLoadedFundraisingProject = async (
  id: string
): Promise<boolean> => {
  if (getFundraisingProjectLoaded() && getFundraisingProject().id === id) {
    return true;
  }
  return false;
};

export const setFundraisingProject = (
  projectDetail: FundraisingDetail
): void => {
  const fundraisingStore = useFundraisingDetailStore();
  fundraisingStore.$patch({
    detail: projectDetail,
  });
};

export const clearFundraisingProjectStore = async (): Promise<void> => {
  useFundraisingDetailStore().$reset();
};

export const getFundraisingProjectLoading = (): boolean => {
  return useFundraisingDetailStore().loading;
};

export const getFundraisingProjectLoaded = (): boolean => {
  return useFundraisingDetailStore().loaded;
};

export const setFundraisingProjectLoading = (value: boolean): void => {
  const fundraisingStore = useFundraisingDetailStore();
  useFundraisingDetailStore().$patch({
    loading: value,
    loaded: value === true ? false : fundraisingStore.loaded,
  });
  if (getFundraisingProjectLoading()) {
    clearFundraisingProjectStore();
    setLoading();
  }
};

export const setFundraisingProjectLoaded = (value: boolean): void => {
  const fundraisingStore = useFundraisingDetailStore();
  fundraisingStore.$patch({
    loading: value === true ? false : fundraisingStore.loading,
    loaded: value,
  });
  if (getFundraisingProjectLoaded()) {
    clearLoading();
  }
};

export const getProjectDonations = (): FundraisingDonationModel[] => {
  const projectsStore = useFundraisingDetailStore();
  return projectsStore.getDonations;
};

// Pagination
export const resetPagination = (): void => {
  const projectsStore = useFundraisingDetailStore();
  projectsStore.resetProjectDetailPagination();
};

export const getPaginationPage = (): number => {
  const projectsStore = useFundraisingDetailStore();
  return projectsStore.getProjectDetailPagination.page;
};

export const setPaginationPage = (value: number): void => {
  const projectsStore = useFundraisingDetailStore();
  projectsStore.setPaginationPage(value);
};

export const getPaginationPageSize = (): number => {
  const projectsStore = useFundraisingDetailStore();
  return projectsStore.getProjectDetailPagination.pageSize;
};

export const getPaginationSort = (): string | undefined => {
  const projectsStore = useFundraisingDetailStore();
  return projectsStore.getProjectDetailPagination.sort;
};
